import {
  Link,
  Trans,
  useI18next,
  useTranslation,
} from "gatsby-plugin-react-i18next";
import * as React from "react";
import { useConfig } from "../../hooks/useConfig";
import { usePushGtmEvent } from "../../hooks/useGmtPushEvent";
import FormButton from "../form/Button";
import Section from "../layout/Section";
import BodyText from "../typography/BodyText";
import { ChevronRightIcon } from "@heroicons/react/solid";
// @ts-ignore
import Carlo from "../../assets/images/sales-rappresentative.jpg";
import { useScheduleCallUrl } from "../../hooks/useScheduleCallUrl";
import { Input, Select } from "../form/Input";
import { TitleHeading } from "../typography/TitleHeading";
import { Pattern } from "../Pattern";
import { useI18nLink } from "../../hooks/useI18nLink";

const initialFormData = {
  name: "",
  email: "",
  message: "",
  budget: "" as string | number,
  tel: "",
  privacy1: false,
  privacy2: false,
};

const ContactUs = (props: {
  variant?: "accent" | "primary";
  withCall?: boolean;
}) => {
  const [formData, updateFormData] = React.useState(initialFormData);
  const [sending, setSending] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const i18nLink = useI18nLink();

  const { contactUs } = useConfig();
  const callUrl = useScheduleCallUrl();
  const { t } = useTranslation();
  const pushGtmEvent = usePushGtmEvent();
  const { navigate } = useI18next();

  const budgets = [
    { value: 5_000, label: t("contactUsSection.budget.options.under_10k") },
    { value: 20_000, label: t("contactUsSection.budget.options.10k_30k") },
    { value: 40_000, label: t("contactUsSection.budget.options.30k_50k") },
    { value: 75_000, label: t("contactUsSection.budget.options.50k_100k") },
    { value: 100_000, label: t("contactUsSection.budget.options.over_100k") },
  ];

  const onFormSubmit = async (event: any) => {
    event.preventDefault();
    setSending(true);
    setShowError(false);

    try {
      const [firstName, lastName] = formData.name.split(" ");
      const budget = budgets.find(
        (b) => b.value.toString() === formData.budget,
      );
      const res = await fetch(contactUs.endpoint, {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          budget: budget?.label || formData.budget,
          firstName,
          lastName,
          referrer: window.location.href,
        }),
      });

      if (!res.ok) throw res;

      navigate("/contact/thanks");
      pushGtmEvent({
        event: "contact_form_submit",
        currency: "EUR",
        value: budget?.value || 0,
      });
    } catch (e) {
      console.error(e);
      setShowError(true);
      setSending(false);
    }
  };

  const handleFormChange = (event: any) => {
    const val =
      event.target.name === "privacy1" || event.target.name === "privacy2"
        ? event.target.checked
        : event.target.value.trim();

    updateFormData({
      ...formData,
      [event.target.name]: val,
    });
  };

  const call = props.withCall !== false && (
    <>
      <div className="flex items-center space-x-4 my-12 w-full">
        <div className="border-t border-gray-100 dark:border-gray-800 grow" />
        <span className="text-gray-400 text-sm font-semibold uppercase">
          {t("contactUsSection.call.or")}
        </span>
        <div className="border-t border-gray-100 dark:border-gray-800 grow" />
      </div>
      <a
        className="space-x-2 flex items-center justify-between cursor-pointer group rounded-2xl"
        href={callUrl}
        target="_blank"
      >
        <div className="flex items-center space-x-3 sm:space-x-6">
          <img
            src={Carlo}
            className="w-16 md:w-20 h-32 rounded-xl object-cover border border-gray-300 dark:border-gray-700"
          />
          <div>
            <span className="text-primary tracking-widest text-xs font-extrabold uppercase">
              {t("contactUsSection.call.tag")}
            </span>
            <p className="text-gray-900 dark:text-white font-extrabold leading-6 text-xl mb-1">
              {t("contactUsSection.call.title")}
            </p>
            <p className="leading-tight text-sm text-gray-600 dark:text-gray-400 max-w-xs">
              {t("contactUsSection.call.subtitle")}
            </p>
          </div>
        </div>
        <div className="h-32 justify-self-end flex items-center justify-center px-1 sm:px-3 py-3 text-base font-medium rounded-2xl text-indigo-700 border border-indigo-300 bg-indigo-100 hover:bg-indigo-200 group-hover:bg-indigo-200 dark:text-indigo-50 dark:border-indigo-700 dark:bg-indigo-900 dark:hover:bg-indigo-800 dark:group-hover:bg-indigo-800">
          <ChevronRightIcon className="inline h-8 w-8 -mt-[1px] ml-1" />
        </div>
      </a>
    </>
  );

  return (
    <Section
      id="contact-form"
      className="border-t dark:border-gray-800 pt-24 flex flex-wrap"
    >
      <div className="mt-0.5 xl:w-1/2 lg:pr-16">
        <div className="w-full mb-6">
          <TitleHeading
            theme="white"
            tag={t("contactUsSection.tag")}
            title={
              <Trans
                i18nKey="contactUsSection.title"
                components={{ ol: <span className="text-ol-gradient" /> }}
              />
            }
          />
          <BodyText className="mt-6">{t("contactUsSection.content")}</BodyText>

          <div className="hidden xl:block">{call}</div>
        </div>
      </div>
      <div className="xl:w-1/2 w-full sm:mt-0">
        <form onSubmit={onFormSubmit}>
          <div className="flex flex-wrap lg:pr-7">
            <Input
              label={t("contactUsSection.name.label")}
              id="name"
              name="name"
              type="text"
              placeholder={t("contactUsSection.name.placeholder")}
              mandatory
              handleFormChange={handleFormChange}
              sending={sending}
              className="w-full"
            />
            <Input
              label={t("contactUsSection.email.label")}
              id="email"
              name="email"
              type="email"
              placeholder={t("contactUsSection.email.placeholder")}
              mandatory
              handleFormChange={handleFormChange}
              sending={sending}
              className="w-full lg:w-1/2 pr-2"
            />
            <Input
              label={t("contactUsSection.tel.label")}
              id="tel"
              name="tel"
              type="tel"
              placeholder={t("contactUsSection.tel.placeholder")}
              mandatory
              handleFormChange={handleFormChange}
              sending={sending}
              className="w-full lg:w-1/2"
            />
            <Input
              label={t("contactUsSection.message.label")}
              id="message"
              name="message"
              type="textarea"
              placeholder={t("contactUsSection.message.placeholder")}
              mandatory
              handleFormChange={handleFormChange}
              sending={sending}
              className="w-full"
            />
            <Select
              label={t("contactUsSection.budget.label")}
              id="budget"
              name="budget"
              options={budgets}
              placeholder={t("contactUsSection.budget.placeholder")}
              mandatory
              handleFormChange={handleFormChange}
              sending={sending}
              className="w-full"
            />
            <div className="py-2 w-full">
              <div className="flex space-x-2 items-center mt-2">
                <input
                  type="checkbox"
                  name="privacy1"
                  onChange={handleFormChange}
                  required
                  className="cursor-pointer h-6 w-6 shadow-sm rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-indigo-500 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
                />
                <label className="text-xs text-gray-500 dark:text-gray-400">
                  <Trans
                    i18nKey="contactUsSection.privacy.check1"
                    components={{
                      policy: (
                        <a
                          href={i18nLink("/privacy/privacy-policy")}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        />
                      ),
                    }}
                  />
                </label>
              </div>
              <div className="flex space-x-2 items-center mt-2">
                <input
                  type="checkbox"
                  name="privacy2"
                  onChange={handleFormChange}
                  className="cursor-pointer h-6 w-6 shadow-sm rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-indigo-500 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
                />
                <label className=" text-xs text-gray-500 dark:text-gray-400">
                  {t("contactUsSection.privacy.check2")}
                </label>
              </div>
            </div>
            <div className="py-2 w-full">
              {showError && (
                <div className="w-full mb-4 py-3 px-5 bg-red-50 rounded-2xl text-red-700 prose prose-strong:text-red-700 prose-a:text-red-700 max-w-none leading-snug border border-red-200 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 dark:prose-invert dark:prose-strong:text-red-300 dark:prose-a:text-red-300">
                  <Trans
                    i18nKey="contactUsSection.error"
                    components={{
                      m: (
                        <a
                          href={`mailto:${contactUs.email}`}
                          className="underline font-semibold"
                        />
                      ),
                    }}
                  />
                </div>
              )}
              <FormButton
                variant={props.variant ?? "accent"}
                type="submit"
                loading={sending}
                disabled={sending}
                text={t("contactUsSection.send")}
                loadingText={t("contactUsSection.sending")}
                className="mt-3"
              />
              <p
                className="text-gray-600 dark:text-gray-400 italic text-center xl:text-right max-w-xs mx-auto xl:mr-0 mt-2 leading-5"
                dangerouslySetInnerHTML={{
                  __html: t("contactUsSection.notes"),
                }}
              />
            </div>
          </div>
        </form>
      </div>
      <div className="xl:hidden grow">{call}</div>

      {props.withCall === false && (
        <Pattern
          type="square"
          className={
            "hidden xl:block absolute h-1/6 xl:h-1/3 w-1/3 bottom-12 left-0 text-gray-200 dark:text-gray-800 opacity-70"
          }
        />
      )}
    </Section>
  );
};

export default ContactUs;
